"use client";

import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Underline from "@tiptap/extension-underline";

import "./CommentEditor.css";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSubscript, faSuperscript } from "@fortawesome/free-solid-svg-icons";

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

const CommentEditor = ({
  onSubmit,
  onChange,
  showSubmitButton = true,
  submitLabel = "Post Comment",
  withTopSpacing = true,
  className = "",
  initialContent = "",
  showAlignmentControls = false,
}) => {
  const [content, setContent] = useState(initialContent || "");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Subscript,
      Superscript,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
    ],
    content: initialContent || "",
    onUpdate: ({ editor }) => {
      const updatedContent = editor.getHTML();
      setContent(updatedContent);
      if (onChange) onChange(updatedContent);
    },
    immediatelyRender: false,
  });

  useEffect(() => {
    if (!editor) return;
    const nextContent = initialContent || "";
    if (editor.getHTML() !== nextContent) {
      editor.commands.setContent(nextContent);
      setContent(nextContent);
    }
  }, [initialContent, editor]);

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert("Image size exceeds 1MB. Please upload a smaller image.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result;
      if (base64 && editor) {
        editor.chain().focus().setImage({ src: base64 }).run();
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = () => {
    if (!content.trim()) return;
    if (onSubmit) onSubmit(content);
    editor?.commands.clearContent();
    setContent("");
  };

  if (!editor) {
    return <p>Loading editor...</p>;
  }

  return (
    <div
      className={`comment-editor ${withTopSpacing ? "mt-20" : ""} ${className}`}
    >
      {/* Toolbar */}
      <div className="toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "is-active" : ""}
        >
          <FormatBoldIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "is-active" : ""}
        >
          <FormatItalicIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={editor.isActive("underline") ? "is-active" : ""}
        >
          <FormatStrikethroughIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          disabled={!editor.can().chain().focus().toggleSubscript().run()}
          className={editor.isActive("subscript") ? "is-active" : ""}
        >
          <FontAwesomeIcon icon={faSubscript} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          disabled={!editor.can().chain().focus().toggleSuperscript().run()}
          className={editor.isActive("superscript") ? "is-active" : ""}
        >
          <FontAwesomeIcon icon={faSuperscript} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "is-active" : ""}
        >
          <FormatListBulletedIcon />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive("orderedList") ? "is-active" : ""}
        >
          <FormatListNumberedIcon />
        </button>
        {showAlignmentControls && (
          <>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign("left").run()}
              className={editor.isActive({ textAlign: "left" }) ? "is-active" : ""}
            >
              <FormatAlignLeftIcon />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign("center").run()}
              className={
                editor.isActive({ textAlign: "center" }) ? "is-active" : ""
              }
            >
              <FormatAlignCenterIcon />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              className={editor.isActive({ textAlign: "right" }) ? "is-active" : ""}
            >
              <FormatAlignRightIcon />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().setTextAlign("justify").run()}
              className={
                editor.isActive({ textAlign: "justify" }) ? "is-active" : ""
              }
            >
              <FormatAlignJustifyIcon />
            </button>
          </>
        )}
        <label htmlFor="image-upload">
          <AddPhotoAlternateIcon />
          <input
            id="image-upload"
            type="file"
            accept="image/jpeg, image/jpg, image/png, image/gif"
            onChange={handleImageUpload}
            style={{ display: "none" }}
          />
        </label>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} placeholder="Write your comment here..." />

      {/* Submit Button */}
      {showSubmitButton && (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!content.trim()}
          className="submit-button"
        >
          {submitLabel}
        </button>
      )}
    </div>
  );
};

export default CommentEditor;
